import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Input from '../../common/Input/index'
import Button from '../../common/Button/index'
import Select from '../../common/Select/index'
import { IoPersonCircleOutline, IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5'
import { HiUser } from 'react-icons/hi'
import Typography from '../../common/Typography/index'
import { fetchUser, updateUser, uploadProfilePhoto, fetchAdminProfile } from '../../../state/redux/actions/userActions'
import { FiPlusCircle } from 'react-icons/fi'

const ProfileDetailsForm = () => {
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState(false)
  const [value, setValue] = useState('all')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formData, setFormData] = useState({
    profileImage: null,
    firstName: '',
    lastName: '',
    email: '',
    accessControl: 'all',
    password: '',
    confirmPassword: '',
  })

  const getCurrentUserId = () => {
    return (
      localStorage.getItem('userId') ||
      localStorage.getItem('currentUserId') ||
      localStorage.getItem('user_id') ||
      sessionStorage.getItem('userId') ||
      sessionStorage.getItem('currentUserId')
    )
  }

  const storeUserId = userId => {
    if (userId) {
      localStorage.setItem('userId', userId)
      localStorage.setItem('currentUserId', userId)
    }
  }

  const dispatch = useDispatch()
  const {
    user,
    loading: userLoading,
    error,
    uploadLoading,
    uploadError,
    adminProfile,
    adminProfileLoading
  } = useSelector(state => state.user)

  const options = [{ value: 'all', label: 'All' }]

  useEffect(() => {
    const profileData = adminProfile || user
    if (profileData) {
      if (profileData.id) {
        storeUserId(profileData.id)
      }

      setFormData(prev => ({
        ...prev,
        firstName: profileData.firstName || profileData.first_name || '',
        lastName: profileData.lastName || profileData.last_name || '',
        email: profileData.email || '',
        accessControl: profileData.accessControl || 'all',
        password: '',
        confirmPassword: '',
      }))
      setValue(profileData.accessControl || 'all')

      const baseUrl = process.env.REACT_APP_BASE_API_URL || '';
      const profilePhotoUrl =
        profileData.profileImageUrl ||
        profileData.profileImage ||
        profileData.profile_photo ?
          (profileData.profile_photo.startsWith('http') ?
            profileData.profile_photo :
            `https://swap-space-latest.onrender.com${profileData.profile_photo}`) :
          null

      if (profilePhotoUrl) {
        setPreview(profilePhotoUrl)
        setPreviewError(false)
      }
    }
  }, [adminProfile, user])

  useEffect(() => {
    // Fetch admin profile first, fallback to user if needed
    dispatch(fetchAdminProfile())

    const userId = getCurrentUserId()
    if (userId && !user && !userLoading) {
      dispatch(fetchUser(userId))
    }
  }, [dispatch, user, userLoading])

  const handleChange = e => {
    const { name, value, files } = e.target

    if (name === 'profileImage' && files && files[0]) {
      const file = files[0]

      if (!file.type.startsWith('image/')) {
        setMessage('Please select a valid image file')
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        setMessage('File size must be less than 5MB')
        return
      }

      setFormData(prev => ({ ...prev, [name]: file }))

      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result)
        setPreviewError(false)
      }
      reader.readAsDataURL(file)
    } else if (name === 'accessControl') {
      setValue(value)
      setFormData(prev => ({ ...prev, [name]: value }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      if (!formData.firstName.trim()) {
        throw new Error('First name is required')
      }
      if (!formData.lastName.trim()) {
        throw new Error('Last name is required')
      }
      if (!formData.email.trim()) {
        throw new Error('Email is required')
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address')
      }

      if (formData.password || formData.confirmPassword) {
        if (!formData.password.trim()) {
          throw new Error('Password is required')
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match')
        }
        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters long')
        }
      }

      let profileImageData = null

      if (formData.profileImage) {
        const photoResult = await dispatch(uploadProfilePhoto(formData.profileImage))
        if (uploadProfilePhoto.rejected.match(photoResult)) {
          const errorMessage = typeof photoResult.payload === 'string'
            ? photoResult.payload
            : photoResult.payload?.message || 'Failed to upload profile image'
          throw new Error(errorMessage)
        }
        profileImageData = photoResult.payload
      }

      const profileData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        first_name: formData.firstName.trim(), // Send both camelCase and snake_case
        last_name: formData.lastName.trim(),   // for API compatibility
        email: formData.email.trim(),
        accessControl: formData.accessControl,
      }

      if (formData.password.trim()) {
        profileData.password = formData.password.trim()
      }

      if (profileImageData) {
        profileData.profileImage = profileImageData.profileImage
        profileData.profileImageUrl = profileImageData.profileImageUrl || profileImageData.imageUrl
        profileData.profile_photo = profileImageData.profileImage || profileImageData.profile_photo
      }

      const profileResult = await dispatch(updateUser(profileData))
      if (updateUser.rejected.match(profileResult)) {
        const errorMessage = typeof profileResult.payload === 'string'
          ? profileResult.payload
          : profileResult.payload?.message || 'Failed to update profile'
        throw new Error(errorMessage)
      }

      setMessage('Profile updated successfully!')

      setFormData(prev => ({
        ...prev,
        profileImage: null,
        password: '',
        confirmPassword: '',
      }))

      const fileInput = document.getElementById('profileImage')
      if (fileInput) {
        fileInput.value = ''
      }

      // Refetch admin profile and user data
      await dispatch(fetchAdminProfile())
      const userId = getCurrentUserId()
      if (userId) {
        dispatch(fetchUser(userId))
      }

      // Emit profile photo updated event for Header component
      if (formData.profileImage || profileImageData) {
        window.dispatchEvent(new CustomEvent('profilePhotoUpdated'))
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      setMessage(error.message || 'Failed to update profile. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if ((userLoading && !user) || (adminProfileLoading && !adminProfile)) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <span className="ml-2 text-gray-600">Loading profile...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="text-red-600 text-center py-4">
          <Typography variant="body">Error loading profile: {error}</Typography>
          <Button onClick={() => window.location.reload()} className="mt-2" size="small">
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <div className="mb-4">
        <p className="text-gray-600 text-sm">
          Edit your profile details, contact info, and preferences easily
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-md ${message.includes('successfully') ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <Typography className={`text-sm ${message.includes('successfully') ? 'text-green-800' : 'text-red-800'}`}>
            {message}
          </Typography>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Profile Photo Section */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center relative overflow-hidden">
            {preview && !previewError ? (
              <img
                src={preview}
                alt="Profile preview"
                className="h-16 w-16 rounded-full object-cover"
                onError={() => {
                  console.error('Profile preview image failed to load:', preview)
                  setPreviewError(true)
                }}
                onLoad={() => {
                  setPreviewError(false)
                }}
              />
            ) : (
              <HiUser className="w-8 h-8 text-teal-600" />
            )}
          </div>
          <div>
            <label htmlFor="profileImage" className="cursor-pointer">
              <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                <FiPlusCircle className="mr-2" /> Add photo
              </span>
            </label>
            <input
              type="file"
              id="profileImage"
              name="profileImage"
              accept="image/*"
              className="hidden"
              onChange={handleChange}
            />
            {uploadLoading && <p className="text-sm text-blue-600 mt-1">Uploading...</p>}
          </div>
        </div>

        {/* Form Fields in 2-column grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm text-gray-700 mb-1">
              First Name
            </label>
            <Input
              id="firstName"
              type="text"
              name="firstName"
              value={formData.firstName}
              placeholder="Enter here"
              onChange={handleChange}
              className="w-full rounded-md border-gray-300"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm text-gray-700 mb-1">
              Last Name
            </label>
            <Input
              id="lastName"
              type="text"
              name="lastName"
              value={formData.lastName}
              placeholder="Enter here"
              onChange={handleChange}
              className="w-full rounded-md border-gray-300"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm text-gray-700 mb-1">
              Email
            </label>
            <Input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              placeholder="Enter here"
              onChange={handleChange}
              className="w-full rounded-md border-gray-300"
              required
            />
          </div>

          {/* Access Control */}
          <div>
            <label htmlFor="accessControl" className="block text-sm text-gray-700 mb-1">
              Access Control
            </label>
            <Select 
              id="accessControl" 
              name="accessControl" 
              options={options} 
              value={value} 
              onChange={handleChange}
              className="w-full rounded-md"
            />
          </div>

          {/* Create Password */}
          <div>
            <label htmlFor="password" className="block text-sm text-gray-700 mb-1">
              Create Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                placeholder="••••••••"
                onChange={handleChange}
                className="w-full pr-10 rounded-md border-gray-300"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <IoEyeOffOutline className="h-5 w-5 text-gray-400" />
                ) : (
                  <IoEyeOutline className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Re-enter Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm text-gray-700 mb-1">
              Re-enter Password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                placeholder="••••••••"
                onChange={handleChange}
                className="w-full pr-10 rounded-md border-gray-300"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <IoEyeOffOutline className="h-5 w-5 text-gray-400" />
                ) : (
                  <IoEyeOutline className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-2">
          <Button 
            type="submit" 
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium px-8 py-2"
            disabled={loading || uploadLoading}
          >
            {loading ? 'Saving...' : uploadLoading ? 'Uploading...' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default ProfileDetailsForm